import type { OptionDefinition, RegisteredOption } from './functionality';

const selectionColorOption: OptionDefinition = {
  type: 'color',
  name: 'Selection Color',
  description: 'Background color for selected text. Can be transparent.',
  path: ['xterm', 'theme', 'selection'],
};
const selectionColorOpacityOption: OptionDefinition = {
  type: 'number',
  name: 'Selection Color Opacity',
  description:
    'Opacity of the selection highlight. A value between 1 (fully opaque) and 0 (fully transparent).',
  path: ['wettyVoid'],
  float: true,
  min: 0,
  max: 1,
};

export const colorThemeOptions: OptionDefinition[] = [
  {
    type: 'color',
    name: 'Foreground Color',
    description: 'The default foreground (text) color.',
    path: ['xterm', 'theme', 'foreground'],
  },
  {
    type: 'color',
    name: 'Background Color',
    description: 'The default background color.',
    path: ['xterm', 'theme', 'background'],
  },
  {
    type: 'color',
    name: 'Cursor Color',
    description: 'Color of the cursor.',
    path: ['xterm', 'theme', 'cursor'],
  },
  {
    type: 'color',
    name: 'Block Cursor Accent Color',
    description:
      'The accent color of the cursor, used as the foreground color for block cursors.',
    path: ['xterm', 'theme', 'cursorAccent'],
  },
  selectionColorOption,
  selectionColorOpacityOption,
  {
    type: 'color',
    name: 'Black',
    description: 'Color for ANSI Black text.',
    path: ['xterm', 'theme', 'black'],
  },
  {
    type: 'color',
    name: 'Red',
    description: 'Color for ANSI Red text.',
    path: ['xterm', 'theme', 'red'],
  },
  {
    type: 'color',
    name: 'Green',
    description: 'Color for ANSI Green text.',
    path: ['xterm', 'theme', 'green'],
  },
  {
    type: 'color',
    name: 'Yellow',
    description: 'Color for ANSI Yellow text.',
    path: ['xterm', 'theme', 'yellow'],
  },
  {
    type: 'color',
    name: 'Blue',
    description: 'Color for ANSI Blue text.',
    path: ['xterm', 'theme', 'blue'],
  },
  {
    type: 'color',
    name: 'Magenta',
    description: 'Color for ANSI Magenta text.',
    path: ['xterm', 'theme', 'magenta'],
  },
  {
    type: 'color',
    name: 'Cyan',
    description: 'Color for ANSI Cyan text.',
    path: ['xterm', 'theme', 'cyan'],
  },
  {
    type: 'color',
    name: 'White',
    description: 'Color for ANSI White text.',
    path: ['xterm', 'theme', 'white'],
  },
  {
    type: 'color',
    name: 'Bright Black',
    description: 'Color for ANSI Bright Black text.',
    path: ['xterm', 'theme', 'brightBlack'],
  },
  {
    type: 'color',
    name: 'Bright Red',
    description: 'Color for ANSI Bright Red text.',
    path: ['xterm', 'theme', 'brightRed'],
  },
  {
    type: 'color',
    name: 'Bright Green',
    description: 'Color for ANSI Bright Green text.',
    path: ['xterm', 'theme', 'brightGreen'],
  },
  {
    type: 'color',
    name: 'Bright Yellow',
    description: 'Color for ANSI Bright Yellow text.',
    path: ['xterm', 'theme', 'brightYellow'],
  },
  {
    type: 'color',
    name: 'Bright Blue',
    description: 'Color for ANSI Bright Blue text.',
    path: ['xterm', 'theme', 'brightBlue'],
  },
  {
    type: 'color',
    name: 'Bright Magenta',
    description: 'Color for ANSI Bright Magenta text.',
    path: ['xterm', 'theme', 'brightMagenta'],
  },
  {
    type: 'color',
    name: 'Bright White',
    description: 'Color for ANSI Bright White text.',
    path: ['xterm', 'theme', 'brightWhite'],
  },
];

function asRegisteredOption(option: OptionDefinition): RegisteredOption {
  if (option.el == null || option.get == null || option.set == null) {
    throw new Error(`Option was not registered: ${option.name}`);
  }
  return option as RegisteredOption;
}

function requireInput(option: RegisteredOption): HTMLInputElement {
  const input = option.el.querySelector<HTMLInputElement>('input');
  if (input == null) {
    throw new Error(`Option input was not rendered: ${option.name}`);
  }
  return input;
}

export function configureColorThemeOptions(): void {
  const selectionColor = asRegisteredOption(selectionColorOption);
  const selectionOpacity = asRegisteredOption(selectionColorOpacityOption);
  selectionColor.get = () => {
    const color = requireInput(selectionColor).value;
    const opacity = Number(requireInput(selectionOpacity).value);
    return color + Math.round(opacity * 255).toString(16);
  };
  selectionColor.set = (value) => {
    const color = String(value);
    requireInput(selectionColor).value = color.substring(0, 7);
    requireInput(selectionOpacity).value = String(
      Math.round((parseInt(color.substring(7), 16) / 255) * 100) / 100,
    );
  };
  selectionOpacity.get = () => 0;
  selectionOpacity.set = () => undefined;
}
